import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';

import { ApiDialogService } from './../../../services/api/api-cdialog.service';
import { IContractInput, EntityEnum } from 'src/app/models/contract';
import {
  DialogTypeEnum,
  IDialogConfirm
} from 'src/app/models/dialog';
import {
  IEditableField,
  IGenericContract,
  IGenericContractFormGroup
} from 'src/app/models/generic';
import { ApiContractService } from 'src/app/services/api/api-contract.service';

@Component({
  selector: 'app-details-form',
  templateUrl: './details-form.component.html',
  styleUrls: ['./details-form.component.scss']
})
export class DetailsFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() tabTitle = '';

  isEditMode = false;

  readonly!: boolean;

  form!: FormGroup<IGenericContractFormGroup>;

  path = '';

  inputControlsFromResolver: IContractInput[] = [];
  invoiceBeneficiariesControls: IContractInput[] = [];
  transferBeneficiariesControls: IContractInput[] = [];

  contractData!: IGenericContract;

  editableFields: IEditableField[] = [];

  editableFieldNames: string[] = [];

  subs: Subscription[] = [];

  stage!: string;

  currentMode: string = '';
  modalOpened: boolean = false;

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _contractService: ApiContractService,
    private _router: Router,
    private _dialogService: ApiDialogService
  ) {
    const routeSub = _activatedRoute.params.subscribe((params) => {
      this.currentMode = params['mode'];
      this.readonly = this.currentMode === 'visualizar';
    });

    this.subs.push(routeSub);

    this.checkIfFormIsReady();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.getDataFromResolver();
  }

  ngOnInit(): void {
    this.stage = this.contractData.StageName;
  }

  ngOnDestroy(): void {
    this.subs.forEach((el) => {
      el.unsubscribe();
    });
  }

  checkIfFormIsReady(): void {
    const checkFormSub = this._contractService.checkIfFormIsReady().subscribe({
      next: (res) => {
        if (res) {
          this.getContractDataAndBuildForm();
        }
      }
    });

    this.subs.push(checkFormSub);
  }

  getDataFromResolver(): void {
    const getDataSub = this._activatedRoute.parent?.data.subscribe({
      next: (data) => {
        this.inputControlsFromResolver = data['contracts'][`${this.tabTitle}`];
        this.invoiceBeneficiariesControls =
          data['contracts'][`beneficiarioParceiro`];
        this.transferBeneficiariesControls =
          data['contracts'][`beneficiarioSubsidiaria`];
      }
    });

    if (getDataSub) {
      this.subs.push(getDataSub);
    }
  }

  getContractDataAndBuildForm(): void {
    this.form = this.formFromService;
    this.contractData = this.contractDataFromService;

    this.editableFields = this.contractData.EditableFields.filter(
      (el) => el.Entity !== EntityEnum.Beneficiary
    );

    this.editableFieldNames = this.editableFields.map((el) => el.Field);
  }

  get formFromService() {
    return this._contractService.getForm();
  }

  get beneficiariesFromService() {
    return this._contractService.getForm()?.controls.Beneficiaries.controls;
  }

  get contractDataFromService() {
    return this._contractService.contractData;
  }

  handleChangeMode(currentMode: string): void {
    if (currentMode === 'visualizar' && !this.modalOpened) {
      let batchNumber: string = this._contractService.batchDetailsNumber;
      let contractData: IGenericContract = this._contractService.contractData;

      if (batchNumber) {
        this.openDialogConfirmation(batchNumber, contractData);
        return;
      }
    }

    this.navigateToCurrentMode();
  }

  openDialogConfirmation(
    batchNumber: string,
    contractData: IGenericContract
  ): void {
    const dialogData: IDialogConfirm = {
      type: DialogTypeEnum.Optional,
      appearance: 'modern',
      template: {
        boldTitle: 'Confirmar edição do contrato',
        title: `Ao editar o contrato ${contractData.Number}, o lote ${batchNumber} será cancelado e um novo lote será gerado após a aprovação da edição solicitada. Deseja prosseguir?`,
        confirm: 'Sim',
        dismiss: 'Não'
      }
    };

    this._dialogService.openDialog(dialogData).then((response) => {
      if (response) {
        this.modalOpened = true;
        this.navigateToCurrentMode();
      }
    });
  }

  async navigateToCurrentMode(): Promise<void> {
    const routeParentParams = await firstValueFrom(
      (this._activatedRoute.parent as ActivatedRoute).params
    ).then();

    const contractNumber = routeParentParams['contractNumber'];
    const financialInstitution = routeParentParams['financialInstitution'];
    const queryNumber = routeParentParams['queryNumber'];   

    const routeParams = await firstValueFrom(
      this._activatedRoute.params
    ).then();

    let routeMode = routeParams['mode'];

    if (routeMode === 'visualizar') {
      this.currentMode = routeMode = 'editar';
    } else if (routeMode === 'editar') {
      this.currentMode = routeMode = 'visualizar';
    }

    this._router.navigate([
      '/contratos',
      contractNumber,
      queryNumber,
      financialInstitution,
      this.tabTitle,
      routeMode
    ]);
  }
}
